import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import {
  transformCamelCaseToTitleCase,
  toCamelCase,
} from "../../../../packages/mfe-core/src/utils/caseUtils";
import { policiesKeys } from "../queryKeys/queryKeys";

import { policiesFindingsApi } from "@repo/api/clients/policiesFindings.api";
import {
  FindingsRequestPayload,
  PolicyFindingsTicketActionPayload,
} from "@repo/api/schemas/policiesFindings.schema";

export const usePolicyFindingsList = (payload: FindingsRequestPayload) => {
  return useInfiniteQuery({
    queryKey: [...policiesKeys.policyFindingsList(), JSON.stringify(payload)],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      try {
        return await policiesFindingsApi.getPolicyFindingsList({
          payload,
          offset: pageParam,
          limit: 20,
        });
      } catch (error) {
        console.error("Failed to fetch Policy List:", error);
        throw error;
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage.nextToken ?? undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};

export const usePolicesFindingsFilters = ({
  policyName = "",
  status = "",
}: {
  policyName?: string;
  status?: string;
} = {}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: policiesKeys.policiesFindingsFilter(),
    queryFn: () => policiesFindingsApi.getPolicyFindingsFilters(),
    retry: false,
  });

  const filtersList = useMemo(
    () =>
      data
        ? Object.entries(data).map(([key, value]) => ({
            label: transformCamelCaseToTitleCase(toCamelCase(key)),
            key,
            options:
              value?.options?.map((option) => ({
                id: option.id,
                label: option.name,
                value: option.id,
                isChecked: false,
              })) || [],
          }))
        : [],
    [data],
  );

  /**
   * Generates initialized filter options for URL parameters.
   * Returns filter options with pre-selected values based on policyName and status.
   */
  const initializedFilterOptions = useMemo(() => {
    if (!filtersList.length || (!policyName && !status)) return {};

    const updates: Record<string, unknown[]> = {};

    const initializeFilter = (
      key: string,
      value: string,
      displayName: string,
    ) => {
      if (!value) return;
      const filter = filtersList.find((f) => f.key === key);
      const lowerValue = value.toLowerCase();

      if (
        filter?.options.some((opt) => opt.label.toLowerCase() === lowerValue)
      ) {
        updates[displayName] = filter.options.map((opt) => ({
          ...opt,
          isChecked: opt.label.toLowerCase() === lowerValue,
        }));
      }
    };

    initializeFilter("policy_name", policyName, "Policy Name");
    initializeFilter("status", status, "Status");

    return updates;
  }, [filtersList, policyName, status]);

  return {
    data: filtersList,
    initializedFilterOptions,
    hasInitializedOptions: Object.keys(initializedFilterOptions).length > 0,
    isError,
    isLoading,
  };
};

export const useFindingsTicketAction = () => {
  return useMutation({
    mutationFn: (payload: PolicyFindingsTicketActionPayload) =>
      policiesFindingsApi.policyFindingsTicketAction(payload),
  });
};

export const useFindingsBulkTicketCreation = (payload: unknown) => {
  return useQuery({
    queryKey: [...policiesKeys.bulkTicketCreation(), payload],
    queryFn: () => policiesFindingsApi.bulkTicketCreation(payload),
    enabled: false,
    retry: false,
  });
};

export const useFindingsCreateTicket = (violationId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => policiesFindingsApi.createTicket(violationId),
    onError: (error) => {
      console.error("Error deleting ticket:", error);
    },
    onSuccess: (data) => {
      console.log("Successfully deleted ticket:", data);
      queryClient.invalidateQueries({
        queryKey: policiesKeys.policyFindingsList(),
      });
    },
  });
};

export const useAccessMAP = (id: string) => {
  return useQuery({
    queryKey: [...policiesKeys.accessMapData(id), id],
    queryFn: () => policiesFindingsApi.getAccessMap(id),
    enabled: true,
    retry: false,
  });
};
